/**
 * Stripe Customer Queries
 *
 * React Query hooks for creating orphan Stripe customers and linking existing Stripe customers.
 * Used only when shouldUseGobodo() is true.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createOrphanStripeCustomer,
  createAndLinkStripeCustomerAccount,
  getStripeCustomerForClientOrAccount,
  type CreateOrphanStripeCustomerInput,
  type CreateAndLinkStripeCustomerInput,
} from '../stripeCustomerService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';

/**
 * Create orphan Stripe customer mutation.
 * Optionally relates the new customer to a Client or Account (clientIdOrAccountId = graph object id).
 */
export function useCreateOrphanStripeCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateOrphanStripeCustomerInput) => createOrphanStripeCustomer(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Link existing Stripe customer (cus_xxx) to the graph.
 * Optionally relates the linked customer to a Client or Account (clientIdOrAccountId = graph object id).
 */
export function useCreateAndLinkStripeCustomerAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAndLinkStripeCustomerInput) =>
      createAndLinkStripeCustomerAccount(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/** Whether Stripe customer create/link is available (Gobodo on) */
export function stripeCustomerHooksAvailable(): boolean {
  return shouldUseGobodo();
}

/**
 * Get the Stripe customer linked to a Client or Account (via relationship).
 * Returns null if none linked or Gobodo is off.
 */
export function useStripeCustomerForClientOrAccount(clientIdOrAccountId: string | number | undefined) {
  const numericId = clientIdOrAccountId != null ? Number(clientIdOrAccountId) : NaN;
  return useQuery({
    queryKey: ['stripeCustomerForClientOrAccount', clientIdOrAccountId],
    queryFn: () => getStripeCustomerForClientOrAccount(numericId),
    enabled: shouldUseGobodo() && !isNaN(numericId),
    staleTime: 2 * 60 * 1000,
  });
}
